// Copyright ©️ 2025 eVolve MEP, LLC

import { useState, useEffect } from 'react';

import { type ChangePasswordBody, useEvolvePost } from '@evolvemep/api-ts';
import { PasswordInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { useWrappedModal } from 'components/Mantine/Modals/WrappedModal';

import { NewPasswordInput } from './NewPasswordInput';

type Props = {
  opened: boolean;
  onClose: () => void;
  onComplete?: () => void;
};

const ResetPasswordForm = ({ onComplete }: Pick<Props, 'onComplete'>) => {
  const { setDisabled } = useWrappedModal();
  const { apiCall: resetPassword } = useEvolvePost('admin/authentication/changePassword');
  const [passwordIsValid, setPasswordIsValid] = useState(false);

  const form = useForm<ChangePasswordBody>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
    },
    validate: {
      currentPassword: (p) => !p,
      newPassword: (p) => !p,
    },
  });

  const onSubmit = async (values: typeof form.values) => {
    await resetPassword(values).then(() => {
      notifications.show({
        title: 'Successfully changed',
        message: 'Password successfully updated.',
        color: 'green',
      });
      onComplete?.();
    });
  };

  useFormModal(form, onSubmit);

  useEffect(() => {
    setDisabled(!form.isDirty() || !form.isValid() || !passwordIsValid);
  }, [form, passwordIsValid, setDisabled]);

  return (
    <>
      <PasswordInput label="Current password" {...form.getInputProps('currentPassword')} autoFocus />
      <NewPasswordInput {...form.getInputProps('newPassword')} setPasswordIsValid={setPasswordIsValid} />
    </>
  );
};

export const ResetPasswordModal = ({ opened, onClose, onComplete }: Props) => (
  <FormModal opened={opened} onClose={onClose} title="Reset password" type="submit">
    <ResetPasswordForm onComplete={onComplete} />
  </FormModal>
);
